import React, { useState } from 'react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { useAccountSettings, SettingsSection } from '@/context/AccountSettingsContext';
import { useBusinessSettings, BusinessSettingsSection } from '@/context/BusinessSettingsContext';
import {
  User,
  CreditCard,
  Shield,
  LogOut,
  Building2,
  Clock,
  Users,
  Plug,
  Star,
  LayoutGrid,
  AlertTriangle,
  Info,
} from 'lucide-react';

interface SettingsOpenMessageProps {
  type: 'account' | 'business';
}

const SettingsOpenMessage: React.FC<SettingsOpenMessageProps> = ({ type }) => (
  <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
    <Info className="h-4 w-4 shrink-0" />
    <span>Click button to close {type} settings</span>
  </div>
);

// Account Settings Hover Menu
interface AccountSettingsHoverMenuProps {
  children: React.ReactNode;
  isSettingsOpen: boolean;
  onLogout: () => void;
  hidePlanButton?: boolean;
}

export const AccountSettingsHoverMenu: React.FC<AccountSettingsHoverMenuProps> = ({
  children,
  isSettingsOpen,
  onLogout,
  hidePlanButton = false,
}) => {
  const isMobile = useIsMobile();
  const { openAccountSettings } = useAccountSettings();
  const [isOpen, setIsOpen] = useState(false);

  // On mobile, just render children without hover functionality
  if (isMobile) {
    return <>{children}</>;
  }

  const handleItemClick = (section: SettingsSection) => {
    setIsOpen(false);
    openAccountSettings(section);
  };

  const handleLogoutClick = () => {
    setIsOpen(false);
    onLogout();
  };

  const accountTabs: { section: SettingsSection; icon: React.ElementType; label: string }[] = [
    { section: 'profile', icon: User, label: 'Profile' },
    ...(hidePlanButton ? [] : [{ section: 'plan' as SettingsSection, icon: CreditCard, label: 'Plan & Usage' }]),
    { section: 'security', icon: Shield, label: 'Security' },
  ];

  return (
    <HoverCard open={isOpen} onOpenChange={setIsOpen} openDelay={0} closeDelay={100}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent align="end" className="w-48 p-1 bg-background">
        <div className="mb-1 px-2 py-1 text-xs text-popover-foreground dark:text-card-foreground">Account Settings</div>
        {isSettingsOpen ? (
          <SettingsOpenMessage type="account" />
        ) : (
          <ButtonGroup orientation="vertical" className="w-full">
            {accountTabs.map(({ section, icon: Icon, label }) => (
              <Button
                key={section}
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => handleItemClick(section)}
              >
                <Icon className="h-4 w-4" />
                {label}
              </Button>
            ))}
            <Button
              variant="destructive"
              size="sm"
              className="w-full justify-start"
              onClick={handleLogoutClick}
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </ButtonGroup>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};

// Business Settings Hover Menu
interface BusinessSettingsHoverMenuProps {
  children: React.ReactNode;
  isSettingsOpen: boolean;
}

export const BusinessSettingsHoverMenu: React.FC<BusinessSettingsHoverMenuProps> = ({
  children,
  isSettingsOpen,
}) => {
  const isMobile = useIsMobile();
  const { openBusinessSettings } = useBusinessSettings();
  const [isOpen, setIsOpen] = useState(false);

  // On mobile, just render children without hover functionality
  if (isMobile) {
    return <>{children}</>;
  }

  const handleItemClick = (section: BusinessSettingsSection) => {
    setIsOpen(false);
    openBusinessSettings(section);
  };

  const businessTabs: { section: BusinessSettingsSection; icon: React.ElementType; label: string }[] = [
    { section: 'business', icon: Building2, label: 'Business' },
    { section: 'hours', icon: Clock, label: 'Hours' },
    { section: 'staff', icon: Users, label: 'Staff' },
    { section: 'reviews', icon: Star, label: 'Reviews' },
    { section: 'integrations', icon: Plug, label: 'Integrations' },
    { section: 'features', icon: LayoutGrid, label: 'Features' },
    { section: 'danger', icon: AlertTriangle, label: 'Danger zone' },
  ];

  return (
    <HoverCard open={isOpen} onOpenChange={setIsOpen} openDelay={0} closeDelay={100}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent align="end" className="w-52 p-1 bg-background">
        <div className="mb-1 px-2 py-1 text-xs text-popover-foreground dark:text-card-foreground">Business Settings</div>
        {isSettingsOpen ? (
          <SettingsOpenMessage type="business" />
        ) : (
          <ButtonGroup orientation="vertical" className="w-full">
            {businessTabs.map(({ section, icon: Icon, label }) => (
              <Button
                key={section}
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => handleItemClick(section)}
              >
                <Icon className="h-4 w-4" />
                {label}
              </Button>
            ))}
          </ButtonGroup>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};
